import { Components, Enums } from '@sendum/core'
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import Select, { StylesConfig } from 'react-select'

import s from './Pallet.module.scss'

export interface ISetPalletFillingModalRef {
    open: (index: number, palletFilling: Enums.PalletFillingType, type: Enums.PalletType) => void
    close: () => void
}

interface IProps {
    setFilling: (index: number, palletFilling: Enums.PalletFillingType) => void
}

export const SetPalletFillingModal = forwardRef<ISetPalletFillingModalRef, IProps>(({ setFilling }, ref) => {
    const selectPalletFillingOptionsWithFull = [
        { value: Enums.PalletFillingType.FULL, label: 'Full' },
        { value: Enums.PalletFillingType.TOP, label: 'Top' },
        { value: Enums.PalletFillingType.BOTTOM, label: 'Bottom' }
    ]

    const selectPalletFillingOptionsWithOutFull = [
        { value: Enums.PalletFillingType.TOP, label: 'Top' },
        { value: Enums.PalletFillingType.BOTTOM, label: 'Bottom' }
    ]

    const [index, setIndex] = useState<number>(0)
    const [palletFilling, setPalletFilling] = useState<Enums.PalletFillingType>(Enums.PalletFillingType.FULL)
    const [selectPalletFillingOptions, setSelectPalletFillingOptions] = useState<
        {
            value: Enums.PalletFillingType
            label: string
        }[]
    >(selectPalletFillingOptionsWithFull)

    const modalRef = useRef<Components.IModalRef>(null)

    const defaultPalletFilling = useMemo(
        () =>
            selectPalletFillingOptions.find(e => {
                return e.value === palletFilling
            }),
        [selectPalletFillingOptions, palletFilling]
    )

    const set = useCallback(() => {
        setFilling(index, palletFilling)
        modalRef.current?.close()
    }, [setFilling, index, palletFilling])

    const actions = useMemo<Components.IAction[]>(
        () => [
            { title: 'Set', variant: 'primary', func: set },
            { title: 'Cancel', variant: 'cancel', func: () => modalRef.current?.close() }
        ],
        [set]
    )

    useImperativeHandle(ref, () => ({
        open(index: number, palletFilling: Enums.PalletFillingType, type: Enums.PalletType) {
            setIndex(index)
            setPalletFilling(palletFilling)
            setSelectPalletFillingOptions(
                type === Enums.PalletType.EXISTING
                    ? [...selectPalletFillingOptionsWithOutFull]
                    : [...selectPalletFillingOptionsWithFull]
            )
            modalRef.current?.open()
        },
        close() {
            modalRef.current?.close()
        }
    }))

    const colourStyles: StylesConfig<any, any> = {
        control: styles => ({ ...styles, ...{ fontSize: '14px' } }),
        input: styles => ({ ...styles, ...{ fontSize: '14px' } }),
        option: styles => ({ ...styles, ...{ fontSize: '14px' } }),
        placeholder: styles => ({ ...styles, ...{ fontSize: '14px' } }),
        singleValue: styles => ({ ...styles, ...{ fontSize: '14px' } })
    }

    return (
        <Components.Modal
            ref={modalRef}
            title="Set pallet filling"
            actions={actions}
            width={200}
            testId="set-pallet-filling-modal"
        >
            <div className={`${s.Pallet} ${s.Pallet__inModal}`}>
                <div className={s.Pallet__column}>
                    <div className={s.Pallet__fullItem}>
                        <span>
                            Select which part of the <br />
                            pallet will be filled
                        </span>
                    </div>
                    <div className={s.Pallet__fullItem}>
                        <Select
                            styles={colourStyles}
                            options={selectPalletFillingOptions}
                            onChange={e => setPalletFilling(e.value)}
                            defaultValue={defaultPalletFilling ?? null}
                        />
                    </div>
                </div>
            </div>
        </Components.Modal>
    )
})
